import { formatCurrency } from '~/lib/CurrencyHelpers';
import SeriesTime from '~/components/forms/questions/scheduling/SeriesTime';
import { useIsWaitlistEnabled } from '~/components/waitlist/AddToWaitlistModal';
import { InProgressBanner } from '~/components/forms/questions/scheduling/SeriesPicker.jsx';
import { useState } from 'react';
import { FormPart, FormStateName, part, state } from '~/lib/FormCssApi';

export function isInstanceSelected(instance, selectedClasses) {
    if (!selectedClasses) {
        return false;
    }

    return !!selectedClasses.find(selected => {
        return (
            selected.eventType === instance.event_type_id &&
            selected.bookableResource === instance.bookable_resource_id &&
            selected.startTime === instance.start
        );
    });
}

export const ClassSpotsLeft = ({
    instance,
    settings,
    className,
    textSize = 'text-sm',
}) => {
    const isWaitlistEnabled = useIsWaitlistEnabled(settings);

    return (
        <div
            {...part(FormPart.CLASS_SPOTS)}
            {...state(instance.spots_left <= 0 && FormStateName.FULL)}
            className={`text-gray-500 ${textSize} ${className}`}
        >
            {!settings?.hideSpotsLeft && instance.spots_left > 0 && (
                <>{instance.spots_left} spots left</>
            )}
            {instance.spots_left <= 0 && (
                <>
                    {isWaitlistEnabled ? 'Full, join waitlist' : 'This is full'}
                </>
            )}
        </div>
    );
};

export const ClassTitle = ({ type, hideTypeName, settings }) => {
    return (
        <>
            {!hideTypeName && (
                <div
                    {...part(FormPart.CLASS_TITLE)}
                    className="text-lg font-medium"
                >
                    {type.name}
                </div>
            )}
            {type.price > 0 && !settings?.hidePrice && (
                <div
                    {...part(FormPart.SELECTION_PRICE)}
                    className="text-gray-500"
                >
                    {formatCurrency(type.price)}
                </div>
            )}
        </>
    );
};

const SeriesTimes = ({truncateSeriesTimes, seriesTimes, duration, clientTz }) => {
    const [showAllTimes, setShowAllTimes] = useState(!truncateSeriesTimes);
    const previewLength = 3;
    const bufferLength = 3;
    const shouldTruncate = !showAllTimes && seriesTimes.length > (previewLength+bufferLength);
    const hasMoreTimes = seriesTimes.length > (previewLength+bufferLength);

    return (
        <>
            <ul className="text-sm mt-2">
                {(!shouldTruncate ? seriesTimes : seriesTimes.slice(0, previewLength)).map((time, idx) => (
                    <li key={idx}>
                        <SeriesTime start={time} duration={duration} clientTz={clientTz} />
                    </li>
                ))}
            </ul>
            {hasMoreTimes && (
                <button
                    type="button"
                    className="ml-3 mb-4 text-primary-700 text-sm themed-text underline"
                    onClick={() => setShowAllTimes(!showAllTimes)}
                >
                    {showAllTimes
                        ? 'Show less'
                        : `+${seriesTimes.length - previewLength} more, view all ${seriesTimes.length} times`
                    }
                </button>
            )}
        </>
    )
}

const ClassDescription = ({ type, instance, className, settings, truncateSeriesTimes, clientTz }) => {
    return (
        <div className={className}>
            {!!instance.settings?.label && (
                <div className="text-gray-500 mt-1 leading-tight whitespace-pre-line">
                    {instance.settings?.label}
                </div>
            )}

            {!!type.description && !settings?.hideDescription && (
                <div className="text-gray-500 mt-1 leading-tight whitespace-pre-line">
                    {type.description}
                </div>
            )}
            {!!instance.in_progress && (
                <InProgressBanner
                    remaining={instance.in_progress_remaining}
                    total={instance.in_progress_total}
                />
            )}
            {type.is_series && instance.series_times?.length > 0 && (
                <SeriesTimes
                    truncateSeriesTimes={truncateSeriesTimes}
                    seriesTimes={instance.series_times}
                    duration={type.duration}
                    clientTz={clientTz}
                />
            )}
        </div>
    );
};

export default ClassDescription;
